/**
 * PATH: packages/ui/src/components/panelResize.tsx
 * FILE_ID: UI-CMP-PANEL-RESIZE-001
 * PURPOSE: Reusable drag-to-resize divider + persisted-size hook.
 * LANDMINE: Promoted verbatim from bills panelResize. Bills keeps a
 *      re-export shim. Do not copy this body back into bills.
 *
 * FLOW:
 *   pointerdown(coord) → pointermove(Δ·sign → clamp[min,max]) → onChange(next)
 *   onChange(next) → usePersistentSize → localStorage[key]
 */

import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';

/** Clamp a candidate size into [min,max]; NaN collapses to `min`. */
export function clampSize(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function readStored(key: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Like useState<number> but the latest value is mirrored to localStorage
 * so a preferred rail width / email height persists across reloads. Falls
 * back gracefully when storage is unavailable (private mode, quota) — the
 * size still works in-memory for the session.
 */
export function usePersistentSize(
  key: string,
  fallback: number,
): [number, (next: number) => void] {
  const [size, setSize] = useState<number>(() => readStored(key, fallback));
  const set = useCallback(
    (next: number) => {
      setSize(next);
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(key, String(Math.round(next)));
      } catch {
        /* private mode / quota — keep the in-memory size */
      }
    },
    [key],
  );
  return [size, set];
}

function readStoredBool(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    return raw === '1' || raw === 'true';
  } catch {
    return fallback;
  }
}

/**
 * Like useState<boolean> but mirrored to localStorage. Fails soft when
 * storage is unavailable (private mode / quota).
 */
export function usePersistentBool(
  key: string,
  fallback: boolean,
): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => readStoredBool(key, fallback));
  const set = useCallback(
    (next: boolean) => {
      setValue(next);
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(key, next ? '1' : '0');
      } catch {
        /* private mode / quota — keep the in-memory value */
      }
    },
    [key],
  );
  return [value, set];
}

function readStoredJson<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw == null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

/**
 * Like useState<T> but mirrored to localStorage as JSON. Fails soft when
 * storage is unavailable (private mode / quota).
 */
export function usePersistentJson<T>(
  key: string,
  fallback: T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => readStoredJson(key, fallback));
  const set = useCallback(
    (next: T) => {
      setValue(next);
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* private mode / quota — keep the in-memory value */
      }
    },
    [key],
  );
  return [value, set];
}

interface ResizeHandleProps {
  axis: 'x' | 'y';
  /**
   * Maps pointer delta → size delta. Use `-1` when the handle sits on the
   * LEADING edge of the panel it resizes (the right rail's left edge, the
   * bottom pane's top edge); `+1` when it sits on the TRAILING edge (the
   * left rail's right edge).
   */
  sign: 1 | -1;
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  /** Double-click resets to this size when provided. */
  resetTo?: number;
  ariaLabel: string;
  className?: string;
}

/**
 * A thin draggable divider. Stateless w.r.t. size — the parent owns the
 * size and feeds it back via `value`; we only translate pointer motion
 * into clamped `onChange` calls. Pointer capture keeps the drag alive
 * even when the cursor outruns the 4px bar.
 */
export function ResizeHandle({
  axis,
  sign,
  value,
  min,
  max,
  onChange,
  resetTo,
  ariaLabel,
  className,
}: ResizeHandleProps): ReactNode {
  const drag = useRef<{ coord: number; value: number } | null>(null);
  const isX = axis === 'x';

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    drag.current = { coord: isX ? e.clientX : e.clientY, value };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* jsdom / unsupported — drag still works via move handler */
    }
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const cur = isX ? e.clientX : e.clientY;
    onChange(clampSize(d.value + (cur - d.coord) * sign, min, max));
  };
  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      role="separator"
      aria-label={ariaLabel}
      aria-orientation={isX ? 'vertical' : 'horizontal'}
      tabIndex={-1}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={resetTo != null ? () => onChange(resetTo) : undefined}
      className={[
        'group relative z-10 shrink-0 touch-none bg-border/60 transition-colors hover:bg-primary/40',
        isX ? 'w-1 cursor-col-resize' : 'h-1 cursor-row-resize',
        className ?? '',
      ].join(' ')}
    >
      {/* Invisible padded hit area — easier to grab than a 4px bar without
          nudging the layout (the visible bar keeps its 4px footprint). */}
      <span
        aria-hidden
        className={
          isX
            ? 'absolute inset-y-0 -left-1.5 -right-1.5'
            : 'absolute inset-x-0 -top-1.5 -bottom-1.5'
        }
      />
    </div>
  );
}
